import { ErrorMessage, TaAutocomplete } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { EditableCard } from '@features/dashboard/specialistDashboard/editableCard/editableCard';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { Chip } from '@mui/material';
import { mapToDictionary } from '@utils/util.functions';
import { Dictionary } from '@utils/util.types';
import crop from 'assets/icons/crop.svg';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrops, useCropsStore } from 'services/crops';
import { useSpecialties, useSpecialtiesStore } from 'services/specialties';
import { Crop, User, useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';

const offerValidationSchema = Yup.object().shape({
	crops: Yup.array().min(1, 'fieldNameError'),
	specialties: Yup.array().min(1, 'fieldNameError'),
});

const initialValues = {
	crops: [],
	specialties: [],
	service: [],
};

interface OfferFormValues {
	crops: Dictionary[];
	specialties: Dictionary[];
	service: Dictionary[];
}

export const WhatIOffer = ({
	readonly = false,
	user: userProp,
}: {
	readonly?: boolean;
	user?: User | null;
}) => {
	const { t } = useTranslation();
	const { crops } = useCropsStore();
	const { specialties } = useSpecialtiesStore();
	const { user: storeUser } = useUsersStore();

	const mutate = useUpdateUser();

	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const user = userProp || storeUser;

	useCrops();

	useSpecialties();

	const cropsDictionary = useMemo(() => mapToDictionary(crops), [crops]);

	const formik = useFormik<OfferFormValues>({
		initialValues: initialValues,
		validationSchema: offerValidationSchema,
		onSubmit: async (values) => {
			const updatePayload = {
				crops: values.crops.map((crop) => crop.id),
				specialties: values.specialties?.map((specialties) => specialties.id),
			};

			setIsLoading(true);
			try {
				await mutate(updatePayload);
				setOpen(false);
				setSnackbarOpen(true);
			} catch (error) {
				console.error('Update failed:', error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	useEffect(() => {
		if (user) {
			setValues({
				crops: mapToDictionary(user.user_crops),
				specialties: user.user_specialties?.map((spec: any) => ({
					id: spec.id,
					name: spec.name,
				})),
				service: [], // Add appropriate value here
			});
		}
	}, [setValues, user, user?.user_crops, user?.user_specialties]);

	return (
		<EditableCard
			className="responsive-card"
			title={t('whatIOffer')}
			onEdit={() => setOpen(true)}
			readonly={readonly}
		>
			<div className="flex flex-col gap-[20px] pb-5">
				<div className="flex flex-wrap gap-[10px]">
					<img className="text-secondary" src={crop} alt="crop" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">
						{t('specialistOf')}
					</p>
					{user?.user_crops.map((item: Crop) => (
						<Chip
							key={item.id}
							label={item.name}
							variant="outlined"
							sx={{
								borderRadius: '8px',
								border: '1px solid #CED4DA',
								backgroundColor: '#F8F9FA',
								padding: '1px 10px',
								color: '#7B809A',
								fontFamily: 'Roboto',
								fontSize: '16px',
								fontStyle: 'normal',
								fontWeight: 400,
								lineHeight: '140%',
								height: '24px',
								textTransform: 'camelcase',
								'& .MuiChip-label': {
									padding: 0,
								},
							}}
						/>
					))}
				</div>
				<div className="w-full h-[1px] bg-[#E9ECEF]"></div>
				<div className="flex flex-wrap gap-[10px]">
					<SchoolOutlinedIcon className="text-secondary" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">
						{t('specializedIn')}
					</p>
					{user?.user_specialties.map((item: Dictionary) => (
						<Chip
							key={item.id}
							label={item.name}
							variant="outlined"
							sx={{
								borderRadius: '8px',
								border: '1px solid #CED4DA',
								backgroundColor: '#F8F9FA',
								padding: '1px 10px',
								color: '#7B809A',
								fontFamily: 'Roboto',
								fontSize: '16px',
								fontStyle: 'normal',
								fontWeight: 400,
								lineHeight: '140%',
								height: '24px',
								textTransform: 'camelcase',
								'& .MuiChip-label': {
									padding: 0,
								},
							}}
						/>
					))}
				</div>
			</div>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				onClose={() => setOpen(false)}
				title={t('whatIOffer')}
				showActions
			>
				<div className="flex flex-col gap-[20px] pb-5 mt-[20px]">
					<div className="flex flex-col gap-[10px]">
						<span className="roboto-regular text-[16px] leading-[140%]">{t('specialistOf')}</span>
						<div>
							<TaAutocomplete
								options={
									formik.values.crops
										? cropsDictionary.filter(
												(option) =>
													!formik.values.crops.some((selected) => selected.id === option.id)
											)
										: cropsDictionary
								}
								getOptionLabel={(option) => option.name}
								label={t('crops')}
								icon={
									<img
										className={
											formik.touched.crops && formik.errors.crops ? 'error' : 'text-secondary'
										}
										src={crop}
										alt="crop"
									/>
								}
								multiple
								filterSelectedOptions
								value={formik.values.crops}
								onChange={(event, value) => formik.setFieldValue('crops', value)}
								error={Boolean(formik.touched.crops && formik.errors.crops)}
							/>
							{formik.touched.crops && formik.errors.crops && (
								<ErrorMessage
									message={t(formik.errors.crops as string, { fieldName: t('crops') })}
								/>
							)}
						</div>
					</div>
					<div className="flex flex-col gap-[10px]">
						<span className="roboto-regular text-[16px] leading-[140%]">{t('specializedIn')}</span>
						<div>
							<TaAutocomplete
								options={
									formik.values.specialties
										? specialties.filter(
												(option) =>
													!formik.values.specialties.some((selected) => selected.id === option.id)
											)
										: specialties
								}
								getOptionLabel={(option) => option.name}
								label={t('specialty')}
								icon={
									<SchoolOutlinedIcon
										className={
											formik.touched.specialties && formik.errors.specialties
												? 'error'
												: 'text-secondary'
										}
									/>
								}
								multiple
								filterSelectedOptions
								value={formik.values.specialties}
								onChange={(event, value) => formik.setFieldValue('specialties', value)}
								error={Boolean(formik.touched.specialties && formik.errors.specialties)}
							/>
							{formik.touched.specialties && formik.errors.specialties && (
								<ErrorMessage
									message={t(formik.errors.specialties as string, {
										fieldName: t('specialty'),
									})}
								/>
							)}
						</div>
					</div>
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('whatIOffer'),
				})}
			/>
		</EditableCard>
	);
};
